import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import CreatorProfile from './CreatorProfile';
import { client, createCacheKey } from '../lib/sanityClient';

interface Artwork {
  _id: string;
  title: string;
  artist: string;
  artistImageUrl?: string;
  description: string;
  imageUrl: string;
  price: number;
  year: string;
  medium: string;
  edition: {
    total: number;
    available: number;
  };
}

interface FeaturedSection {
  _id: string;
  title: string;
  artworks: Artwork[];
  displayOrder: number;
}

const FeaturedArtworks: FC = () => {
  const { data: featuredSections = [], isLoading } = useQuery<FeaturedSection[]>({
    queryKey: createCacheKey('featured-artworks'),
    queryFn: async () => {
      const query = `*[_type == "homeFeaturedArtworks"] | order(displayOrder asc) {
        _id,
        title,
        displayOrder,
        "artworks": artworks[]->{
          _id,
          title,
          "artist": artist->name,
          "artistImageUrl": artist->image.asset->url,
          "imageUrl": image.asset->url,
          description,
          year,
          medium,
          price,
          "edition": edition->{
            "total": editionSize,
            "available": availableEditions
          }
        }
      }`;
      return client.fetch<FeaturedSection[]>(query);
    }
  });

  if (isLoading) {
    return <div className="py-16 bg-gray-50">Loading...</div>;
  }

  return (
    <div className="space-y-24">
      {featuredSections.map((section) => (
        <section key={section._id} className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-light mb-12">{section.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
              {section.artworks.map((artwork) => (
                <div key={artwork._id} className="group">
                  <div className="relative pb-[100%] mb-6 overflow-hidden">
                    <img
                      src={artwork.imageUrl}
                      alt={artwork.title}
                      className="absolute h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="text-2xl font-ogg font-light not-italic mb-3">{artwork.title}</h3>
                  <div className="mb-4">
                    <CreatorProfile 
                      name={artwork.artist}
                      imageUrl={artwork.artistImageUrl}
                      size="sm"
                      showName={true}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-light">${artwork.price.toLocaleString()}</span>
                    <button className="bg-black text-white px-6 py-2 hover:bg-gray-800 transition-colors">
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default FeaturedArtworks;